import { useEffect, useState } from 'react';
import classNames from 'classnames';
import {
  OPEN_BOOK_JOB_ATTRIBUTE,
  getOpenBookJob,
} from '@modules/InboxModule/lib/manualBookings';
import TasksModule from '@modules/TasksModule';
import useTasks from '@modules/TasksModule/hooks/useTasks';
import CustomerMfeAdapter from '../CustomerMfeAdapter';
import type { ResolvedCustomer } from '../CustomerMfeAdapter';
import type { BookingCompletedPayload } from '../CustomerMfeAdapter/hostOptions';
import { completeManualBooking } from '../ManualBookingCard/closeManualBooking';
import styles from './ManualBookingTaskView.module.css';

type ResolvedSeed = {
  orgNodeId: string;
  customerId: string;
};

type Props = {
  taskId: string;
  /** Whether this card is the selected one. A hidden card stays mounted, so its booking survives a card switch. */
  show?: boolean;
};

/**
 * The view of a manual booking card: customer-mfe, opened on customer search
 * across all tenants. The customer the CSR picks is kept on the card's task,
 * which names the card, and re-seeds the MFE so it stays on that customer.
 * A card started from a customer's Book Job opens Book Job for that customer
 * at once instead, at the location chosen there. There is no call, so no
 * phone, call SID or campaign is passed.
 */
const ManualBookingTaskView = ({ taskId, show = false }: Props) => {
  const { taskById } = useTasks();
  const task = taskById[taskId];
  // Local state wins, so a pick made in this view is never overridden.
  const [resolvedSeed, setResolvedSeed] = useState<ResolvedSeed | undefined>(
    undefined,
  );
  // Read at first mount and kept for this view's life: customer-mfe reads it
  // when its bundle mounts, which can be after the attribute is cleared below.
  const [openBookJob] = useState(() => getOpenBookJob(task));
  // used once: a later remount of this view opens the customer, not Book Job
  useEffect(() => {
    if (openBookJob && TasksModule.getTask(taskId)) {
      TasksModule.updateTaskAttributes(taskId, {
        [OPEN_BOOK_JOB_ATTRIBUTE]: undefined,
      });
    }
  }, [openBookJob, taskId]);
  const taskCustomerId: string | undefined = task?.attributes.customerId;
  const taskOrgNodeId: string | undefined = task?.attributes.customerOrgNodeId;
  const seed =
    resolvedSeed ??
    (taskCustomerId && taskOrgNodeId
      ? { orgNodeId: taskOrgNodeId, customerId: taskCustomerId }
      : undefined);

  const handleCustomerChanged = (customer: ResolvedCustomer) => {
    setResolvedSeed(
      customer?.orgNodeId
        ? { orgNodeId: customer.orgNodeId, customerId: customer.id }
        : undefined,
    );
    TasksModule.setCustomerOnTask(taskId, customer);
  };

  // booking the job closes the card on its own
  const handleBookingCompleted = (booked: BookingCompletedPayload) => {
    completeManualBooking(taskId, booked);
  };

  if (!task) {
    return null;
  }

  return (
    // flex-grow-1 fills InboxPage's column Stack; d-f only while shown, so it
    // cannot defeat the hidden class.
    <div
      className={classNames('of-y-auto', 'flex-grow-1', {
        'd-f': show,
        [styles.hidden]: !show,
      })}
    >
      <CustomerMfeAdapter
        orgNodeId={seed?.orgNodeId}
        customerId={seed?.customerId}
        // the adapter seeds a name only when there is one
        customerName={task.displayName}
        openBookJob={openBookJob !== undefined}
        locationId={openBookJob?.locationId ?? undefined}
        viewKey={task.viewKey}
        onCustomerChanged={handleCustomerChanged}
        onBookingCompleted={handleBookingCompleted}
        // no call to link: a booked job gets the plain toast
        noCallToLink
      />
    </div>
  );
};

export default ManualBookingTaskView;
